/**
 * Disperse annotation positions to avoid overlap.
 * Works in pixel space: each label is ~(charCount*7) x 16 px.
 * Iteratively nudges overlapping labels apart.
 */
export function disperseAnnotations(annotations, plotWidth, plotHeight, margin, xDomain, yDomain) {
  if (annotations.length <= 1) return annotations;

  // Convert data coords to pixel space
  const pxW = plotWidth - margin.l - margin.r;
  const pxH = plotHeight - margin.t - margin.b;
  const xScale = pxW / (xDomain[1] - xDomain[0]);
  const yScale = pxH / (yDomain[1] - yDomain[0]);

  const labels = annotations.map(a => {
    const dataPx = { x: (a.x - xDomain[0]) * xScale, y: (yDomain[1] - a.y) * yScale };
    const labelW = (a.text.replace(/<[^>]+>/g, '').length) * 7 + 10;
    const labelH = 18;
    return {
      ann: a,
      // Label center in px (offset from data point by ax, ay)
      cx: dataPx.x + a.ax,
      cy: dataPx.y + a.ay,
      w: labelW,
      h: labelH,
      dataPx,
    };
  });

  // Iterative repulsion (12 passes)
  for (let iter = 0; iter < 12; iter++) {
    for (let i = 0; i < labels.length; i++) {
      for (let j = i + 1; j < labels.length; j++) {
        const a = labels[i], b = labels[j];
        const overlapX = (a.w + b.w) / 2 - Math.abs(a.cx - b.cx);
        const overlapY = (a.h + b.h) / 2 - Math.abs(a.cy - b.cy);

        if (overlapX > 0 && overlapY > 0) {
          // Push apart along axis of least overlap
          if (overlapX < overlapY) {
            const push = overlapX / 2 + 2;
            if (a.cx < b.cx) { a.cx -= push; b.cx += push; }
            else { a.cx += push; b.cx -= push; }
          } else {
            const push = overlapY / 2 + 2;
            if (a.cy < b.cy) { a.cy -= push; b.cy += push; }
            else { a.cy += push; b.cy -= push; }
          }
        }
      }
    }
  }

  // Clamp labels to stay within plot area (with some padding)
  for (const l of labels) {
    l.cx = Math.max(l.w / 2 + 4, Math.min(pxW - l.w / 2 - 4, l.cx));
    l.cy = Math.max(l.h / 2 + 4, Math.min(pxH - l.h / 2 - 4, l.cy));
  }

  // Write back as ax/ay offsets
  return labels.map(l => ({
    ...l.ann,
    ax: l.cx - l.dataPx.x,
    ay: l.cy - l.dataPx.y,
  }));
}
